import {
  useNavigate,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { MultiStepForm, StepDefinition } from "@/components/multi-step-form";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { validateAuthentication } from "@/utils/auth-middleware";
import { OrderAlert } from "@/components/order-alert";

import {
  StepDadosPrincipais,
  dadosPrincipaisSchema,
  dadosPrincipaisSchemaWithRefinement,
} from "./step-dados-principais";
import { StepMidias, midiasSchema, validateMidiasStep } from "./step-midias";
import {
  StepCaracteristicas,
  caracteristicasSchema,
  validateCaracteristicasStep,
} from "./step-caracteristicas";
import { StepReview, reviewSchema } from "./step-review";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { apiWobaInventory } from "@/services/api-woba-inventory";
import { PrivateOfficeStatus } from "@/@types/private-office.types";
import { AmenitiesData } from "@/components/amenities-form/amenities-form.types";
import { newCreatePrivateOfficeLoader } from "./loader";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { OutlierConfirmationDialog } from "@/components/statistical-alerts";
import { EVENTS, PRODUCT_TYPE } from "@/features/analytics/constants/events";
import { EVENTS_PROPERTIES } from "@/features/analytics/constants/events";
import {
  UploadStateProvider,
  useUploadState,
} from "@/contexts/upload-state-context";
import { useAuthGuard } from "@/hooks/use-auth-guard.ts";
import { PrivateOfficeConfirmationModal } from "@/components/private-office-confirmation-modal";
import {
  getUserType,
  shouldShowModal,
  UserType,
} from "@/utils/user-type-checker";

export { newCreatePrivateOfficeLoader } from "./loader";

const privateOfficeCreateSchema = z.object({
  ...dadosPrincipaisSchema.shape,
  ...midiasSchema.shape,
  ...caracteristicasSchema.shape,
  ...reviewSchema.shape,
});

export type PrivateOfficeCreateData = z.infer<typeof privateOfficeCreateSchema>;

type LoaderData = Awaited<ReturnType<typeof newCreatePrivateOfficeLoader>>;

const transformFormDataToPayload = (
  data: PrivateOfficeCreateData,
  amenitiesData: AmenitiesData | null,
  userEmail: string | null,
) => {
  // Transform photos array to API format - photos should already have URLs from step validation
  const transformedPhotos = (data.photos || []).map((photo: any) => ({
    url: photo.url || "",
    tags: photo.tags || [],
  }));

  // Transform video URL to array format
  const transformedVideo = data.videoUrls?.[0] ? [data.videoUrls[0]] : [];

  // Transform amenities to API format - send full structure like amenities API but with user changes
  const transformedAmenities = amenitiesData
    ? amenitiesData.amenities.map((group) => ({
        name: group.name,
        amenities: group.amenities.map((amenity) => {
          // Find if this amenity was selected/modified by user
          const userAmenity = (data.selectedAmenities || []).find(
            (selected: any) => selected.amenityId === amenity.id,
          );

          const finalStatus = userAmenity ? userAmenity.status : amenity.status;

          return {
            id: amenity.id,
            slug: amenity.slug,
            status: finalStatus,
            name: amenity.name,
            description: amenity.description,
            attributes:
              finalStatus === "included" || finalStatus === "not-included"
                ? [] // Empty array for included/not-included
                : amenity.attributes.map((attr) => ({
                    name: attr.name,
                    content:
                      userAmenity?.attributes?.[attr.name] || attr.content,
                    contentType: attr.contentType,
                  })),
          };
        }),
      }))
    : [];

  // Transform operating hours to backend format
  const transformOperatingHours = (operatingHours: any) => {
    if (!operatingHours) {
      return {};
    }

    const result: any = {};
    const daysOrder = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    daysOrder.forEach((day) => {
      const dayHours = operatingHours[day];
      result[day] = dayHours || [];
    });

    return result;
  };

  const cookies = document.cookie.split(";");
  const spaceIdCookie = cookies
    .find((cookie) => cookie.trim().startsWith("space_id="))
    ?.split("=")[1];

  // Get selected space data - assume we have externalSpaceId from form
  const externalSpaceId = data.externalSpaceId || spaceIdCookie;

  const payload = {
    externalSpaceId: parseInt(externalSpaceId || "0") || 0, // Changed from partnerId to externalSpaceId and convert to number
    name: data.name || "",
    description: data.description || "",
    photos: transformedPhotos,
    videoUrls: transformedVideo, // Changed from video to videoUrls
    capacity: parseInt(data.capacity) || 0,
    sizeM2: parseInt(data.size) || 0,
    monthlyPrice:
      parseFloat(data.monthlyPrice.replace(/\./g, "").replace(",", ".")) || 0,
    status: data.status || PrivateOfficeStatus.AVAILABLE,
    lifecycleState: data.lifecycleState || null,
    availabilityDate: data.availabilityDate || null,
    notes: data.notes || "",
    commercializationTypes: ["private_office"],
    userEmail: userEmail || "",
    operatingHours: transformOperatingHours(data.operatingHours),
    timezone: data.timezone || "America/Sao_Paulo",
    amenities: transformedAmenities,
  };

  return payload;
};

function PrivateOfficeNewCreateContent() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const loaderData = useLoaderData() as LoaderData;
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOutlierModal, setShowOutlierModal] = useState(false);
  const [statisticalData, setStatisticalData] = useState<any>(null);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<PrivateOfficeCreateData | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [createdRoomId, setCreatedRoomId] = useState<string | null>(null);
  const [userType, setUserType] = useState<UserType>("internal");
  const { userInfo } = useAuth();
  const { userEmail, userId, isExternalUser } = useAuthGuard();
  const { uploadState } = useUploadState();

  // Get amenities data from loader
  const amenitiesData = loaderData.amenitiesData;

  // Derive step loading state from upload context
  const stepLoadingState = useMemo(
    () => ({
      isLoading: uploadState.isUploading,
      loadingText: uploadState.uploadingText,
    }),
    [uploadState.isUploading, uploadState.uploadingText],
  );

  useEffect(() => {
    if (!email && userInfo?.profile?.email) {
      setEmail(userInfo?.profile?.email);
    }

    if (!email && userEmail) {
      setEmail(userEmail);
    }
  }, [userEmail, userInfo]);

  useEffect(() => {
    console.log(email);
  }, [email]);

  useEffect(() => {
    const cookies = document.cookie.split(";");
    const spaceIdCookie = cookies
      .find((cookie) => cookie.trim().startsWith("space_id="))
      ?.split("=")[1];

    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    sendTrackDataServer({
      eventName: "PRIVATE_ROOM_INFO_STARTED",
      data: {
        user_id:
          authResult.payload?.id || authResult.payload?.userId || "unknown",
        email: authResult.payload?.email || "unknown",
        flow_type: "create" as const,
        space_id: spaceIdCookie || "unknown",
      },
      url: pathname,
      productType: "PO",
    });
  }, [pathname]);

  // Função interna para executar a submissão real
  const executeSubmission = async (data: PrivateOfficeCreateData) => {
    try {
      setIsSubmitting(true);
      console.log("API Gateway - Form data received:", data);

      // Show initial toast
      toast({
        title: "Criando sala privativa...",
        description: "Finalizando processo",
      });

      // Transform form data to API payload - photos should already have URLs
      const apiPayload = transformFormDataToPayload(data, amenitiesData, email);
      console.log(
        "API Gateway - API Payload:",
        JSON.stringify(apiPayload, null, 2),
      );

      // Show success toast for photo upload
      const validPhotos = (data.photos || []).filter(
        (photo: any) => photo.url,
      ).length;
      if (validPhotos > 0) {
        toast({
          title: "Fotos processadas com sucesso!",
          description: `${validPhotos} foto${validPhotos > 1 ? "s" : ""} incluída${validPhotos > 1 ? "s" : ""}`,
        });
      }

      // Make API call to create private office
      const response = await apiWobaInventory.post(
        "/private-offices",
        apiPayload,
      );

      console.log("Private office created successfully:", response.data);

      const roomId =
        response.data?._id || response.data?.privateOfficeId || null;
      setCreatedRoomId(roomId);

      const cookies = document.cookie.split(";");
      const spaceIdCookie = cookies
        .find((cookie) => cookie.trim().startsWith("space_id="))
        ?.split("=")[1];
      const isExternalRoute = pathname.includes("/external/");
      const authResult = validateAuthentication(isExternalRoute);

      sendTrackDataServer({
        eventName: "CREATE_ROOM_SUCCESS",
        data: {
          privateroom_id: roomId || "unknown",
          space_id: data.externalSpaceId || spaceIdCookie || "unknown",
          user_id:
            authResult.payload?.id || authResult.payload?.userId || "unknown",
          email: authResult.payload?.email || "unknown",
        },
        url: pathname,
        productType: "PO",
      });

      const detectedUserType = getUserType(userId ?? undefined, isExternalUser);
      setUserType(detectedUserType);

      if (!shouldShowModal(detectedUserType)) {
        toast({
          title: "Sala criada com sucesso!",
          description: "A sala foi criada e aprovada automaticamente.",
        });
      } else {
        setShowConfirmationDialog(true);
      }
    } catch (error: any) {
      console.error("API Gateway - Error creating private office:", error);

      const cookies = document.cookie.split(";");
      const spaceIdCookie = cookies
        .find((cookie) => cookie.trim().startsWith("space_id="))
        ?.split("=")[1];
      const isExternalRoute = pathname.includes("/external/");
      const authResult = validateAuthentication(isExternalRoute);

      sendTrackDataServer({
        eventName: "CREATE_ROOM_ERROR",
        data: {
          user_id:
            authResult.payload?.id || authResult.payload?.userId || "unknown",
          email: authResult.payload?.email || "unknown",
          space_id: data.externalSpaceId || spaceIdCookie || "unknown",
          error_message:
            error.response?.data?.message || error.message || "Unknown error",
        },
        url: pathname,
        productType: "PO",
      });

      let errorMessage = "Erro interno do sistema";

      if (error.response) {
        // API returned an error response
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMessage =
            "Dados inválidos. Verifique as informações preenchidas.";
        } else if (status === 401) {
          errorMessage = "Não autorizado. Faça login novamente.";
        } else if (status === 403) {
          errorMessage =
            "Acesso negado. Você não tem permissão para criar salas.";
        } else if (status === 409) {
          errorMessage = "Conflito. Uma sala com este nome já existe.";
        } else if (status >= 500) {
          errorMessage = "Erro no servidor. Tente novamente mais tarde.";
        } else {
          errorMessage = data?.message || `Erro HTTP ${status}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          "Erro de conexão. Verifique sua internet e tente novamente.";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Erro ao criar sala privativa",
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (data: PrivateOfficeCreateData) => {
    // Se há outliers extremos, mostra modal de confirmação
    if (statisticalData?.hasExtremeOutliers?.hasAnyExtreme) {
      setPendingSubmissionData(data);
      setShowOutlierModal(true);
      return; // Interrompe submissão até confirmação do usuário
    }

    // Executa a submissão (normal ou após confirmação)
    await executeSubmission(data);
  };

  // Função chamada quando o usuário confirma no dialog de outliers
  const handleConfirmSubmission = useCallback(async () => {
    setShowOutlierModal(false);
    if (pendingSubmissionData) {
      await executeSubmission(pendingSubmissionData);
      setPendingSubmissionData(null);
    }
  }, [pendingSubmissionData, email]);

  // Função chamada quando o usuário cancela no dialog de outliers
  const handleCancelSubmission = useCallback(() => {
    setShowOutlierModal(false);
    setPendingSubmissionData(null);
  }, []);

  const handleGoBack = () => {
    navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN);
  };

  // Função para lidar com o fechamento do diálogo de confirmação
  const handleCloseConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog(false);
    // Determine redirect route based on returnUrl or user type
    let redirectRoute: string;

    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    if (authResult.isExternal) {
      // External users: redirect to edit page with the created room ID
      if (createdRoomId) {
        redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_EDIT_EXTERNAL.replace(
          ":privateOfficeId",
          createdRoomId,
        );
      } else {
        redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE_EXTERNAL;
      }
    } else {
      redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN;
    }

    navigate(redirectRoute);
  }, [returnUrl, pathname, createdRoomId, navigate]);

  // Callback para receber dados estatísticos do StepReview
  const handleStatisticalDataReady = useCallback((data: any) => {
    setStatisticalData(data);
  }, []);

  // Analytics handler
  const handleAnalyticsEvent = (
    eventName: keyof typeof EVENTS,
    properties: EVENTS_PROPERTIES[keyof typeof EVENTS],
  ) => {
    sendTrackDataServer({
      eventName,
      data: properties,
      url: "/private-office/new-create",
      productType: "PO" as PRODUCT_TYPE,
    });
  };

  // Create wrapper components that inject amenitiesData - memoized to prevent re-creation
  const StepCaracteristicasWithData = useMemo(() => {
    const Wrapper = (props: any) => (
      <StepCaracteristicas {...props} amenitiesData={amenitiesData} />
    );
    Wrapper.displayName = "StepCaracteristicasWithData";
    return Wrapper;
  }, [amenitiesData]);

  const StepReviewWithData = useMemo(() => {
    const Wrapper = (props: any) => (
      <StepReview
        {...props}
        amenitiesData={amenitiesData}
        onStatisticalDataReady={handleStatisticalDataReady}
      />
    );
    Wrapper.displayName = "StepReviewWithData";
    return Wrapper;
  }, [amenitiesData, handleStatisticalDataReady]);

  const steps: StepDefinition[] = useMemo(
    () => [
      {
        id: "dados-principais",
        title: "Dados principais",
        component: StepDadosPrincipais,
        schema: dadosPrincipaisSchemaWithRefinement,
        description: "Informações básicas sobre a sala privativa",
      },
      {
        id: "midias",
        title: "Mídias da sala",
        component: StepMidias,
        schema: midiasSchema,
        description: "Adicione fotos da sala",
        onValidateStep: validateMidiasStep,
      },
      {
        id: "caracteristicas",
        title: "Características e Serviços",
        component: StepCaracteristicasWithData,
        schema: caracteristicasSchema,
        description: "Amenidades e serviços disponíveis",
        onValidateStep: validateCaracteristicasStep,
      },
      {
        id: "review",
        title: "Revise e publique",
        component: StepReviewWithData,
        schema: reviewSchema,
        description: "Confira todas as informações antes de publicar",
        customTitle: "Revise e publique",
      },
    ],
    [StepCaracteristicasWithData, StepReviewWithData],
  );

  const isExternalRoute = pathname.includes("/external/");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FCFBF9" }}>
      {!isExternalRoute && (
        <header className="bg-white border-b-2">
          <nav className="flex items-center justify-between gap-2 p-4">
            <section className="flex items-center gap-2">
              <Button size="icon" variant="outline" onClick={handleGoBack}>
                <ArrowLeft size={20} />
              </Button>
            </section>
          </nav>
        </header>
      )}

      <div className="container mx-auto py-8 px-4 pb-32">
        <MultiStepForm
          title="Criar nova sala privativa"
          description="Preencha as informações da sala para vinculá-la a um espaço e disponibilizá-la na plataforma."
          headerContent={<OrderAlert variant="create" />}
          steps={steps}
          onSubmit={handleSubmit}
          allowClickNavigation={false}
          nextButtonText="Próximo"
          submitButtonText="Publicar sala"
          showStepIndicator={true}
          stepLoadingState={stepLoadingState}
          isSubmitting={isSubmitting}
        />
      </div>

      <OutlierConfirmationDialog
        isOpen={showOutlierModal}
        onClose={handleCancelSubmission}
        onConfirm={handleConfirmSubmission}
        statisticalData={statisticalData?.statisticalData}
        onAnalyticsEvent={handleAnalyticsEvent}
      />

      <PrivateOfficeConfirmationModal
        isOpen={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        userType={userType}
        actionType="create"
      />

      <Toaster />
    </div>
  );
}

export default function PrivateOfficeNewCreate() {
  return (
    <UploadStateProvider>
      <PrivateOfficeNewCreateContent />
    </UploadStateProvider>
  );
}
